import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import html2canvas from 'html2canvas';
import type { StatsResponse } from '@/types/stats';
import { isBiggestGameError } from '@/types/stats';

interface WrappedModalProps {
  data: StatsResponse;
  year: string;
  onClose: () => void;
}

const DEFAULT_AVATAR = 'https://www.chess.com/bundles/web/images/user-image.007dad08.svg';

// ─── Card definitions ──────────────────────────────────────────────────────

interface ScoreBar {
  name: string;
  value: number;
}

type Card =
  | { slug: string; bg: string; kind: 'intro'; username: string; year: string }
  | {
      slug: string; bg: string; kind: 'hero';
      eyebrow: string; hero: string; headline: string; flavor: string;
      secondary?: string; link?: string;
    }
  | {
      slug: string; bg: string; kind: 'split';
      left: { label: string; value: string };
      right: { label: string; value: string };
      headline: string; flavor: string;
    }
  | {
      slug: string; bg: string; kind: 'list';
      headline: string; items: { label: string; value: string }[]; styleTag?: string;
    }
  | { slug: string; bg: string; kind: 'dna'; summary: string; bars: ScoreBar[] }
  | { slug: string; bg: string; kind: 'psychology'; overall: number; verdict: string; bars: ScoreBar[] }
  | { slug: string; bg: string; kind: 'personality'; archetype: string; description: string; traits: string[] }
  | {
      slug: string; bg: string; kind: 'share';
      username: string; avatar: string; year: string;
      archetype: string; stats: { label: string; value: string }[];
    }
  | { slug: string; bg: string; kind: 'outro'; username: string; avatar: string; year: string };

// ─── Small helpers ─────────────────────────────────────────────────────────

const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

function includesAny(text: string, needles: string[]): boolean {
  const t = text.toLowerCase();
  return needles.some((n) => t.includes(n.toLowerCase()));
}

function gamesFlavor(n: number): string {
  if (n < 50) return 'You were testing the waters.';
  if (n < 200) return 'A respectable season.';
  if (n < 500) return 'You were on a mission.';
  if (n < 1000) return 'Chess was basically your job.';
  return "You don't have a problem. You have a passion.";
}

function winRateFlavor(pct: number): string {
  if (pct < 40) return 'Chess is humbling. You keep showing up.';
  if (pct < 50) return 'Right on the edge. The clutch gene is developing.';
  if (pct < 55) return 'More wins than losses. Positive territory.';
  if (pct < 65) return "Winning more than you lose. That's the goal.";
  return 'Dominant. Most players would be satisfied.';
}

function timeFlavor(hours: number): string {
  if (hours < 10) return 'Casual. Every move mattered.';
  if (hours < 50) return 'More than a weekend hobby.';
  if (hours < 100) return "That's a serious time investment.";
  if (hours < 200) return 'Chess took up a real chunk of your year.';
  return 'You practically lived on the board.';
}

function ratingFlavor(rating: number): string {
  if (rating < 800) return 'Everyone starts somewhere.';
  if (rating < 1000) return 'The fundamentals are forming.';
  if (rating < 1200) return 'Solidly intermediate.';
  if (rating < 1400) return 'You know what you are doing.';
  if (rating < 1600) return 'Legitimately strong.';
  if (rating < 1800) return 'Club-level threat.';
  if (rating < 2000) return 'Expert territory.';
  return "That's titled-player range.";
}

function openingStyleTag(white: string, black: string): string {
  if (includesAny(white, ["King's Indian", 'Sicilian', 'Benoni', 'Dutch', 'Pirc'])) {
    return 'Sharp and attacking';
  }
  if (includesAny(white, ["Queen's Gambit", 'London', 'Catalan', 'Reti'])) {
    return 'Positional and solid';
  }
  if (includesAny(black, ['French', 'Caro', 'Slav', 'Petroff'])) {
    return 'Defensive and resilient';
  }
  if (includesAny(black, ["King's Indian", 'Sicilian', 'Nimzo', 'Grunfeld'])) {
    return 'Counter-punching';
  }
  return 'Flexible and pragmatic';
}

function streakHeadline(win: number, loss: number): string {
  if (win / (loss + 1) > 3) return "Built different. Losses don't stick.";
  if (win > 7) return 'Seven in a row. Someone was in the zone.';
  if (loss > win) return 'The losing streak was a chapter. The season was a book.';
  return 'Streaks are part of the game. You had both.';
}

function parseHours(timeSpent: string): number {
  // time_spent looks like "5h 12m" / "2h" / "45m". Pull leading hour count.
  const match = timeSpent.match(/(\d+)\s*h/i);
  return match ? parseInt(match[1], 10) : 0;
}

function busiestFlavor(timeSpent: string): string {
  const hours = parseHours(timeSpent);
  if (timeSpent.toLowerCase().includes('h') && hours > 5) {
    return 'That is a full workday of chess. Priorities: sorted.';
  }
  if (hours > 2) return 'More than two hours in a single sitting.';
  return 'Some days just belong to chess.';
}

function topTimeClass(counts: Record<string, number>): string {
  let best = '';
  let bestN = -1;
  for (const [k, v] of Object.entries(counts)) {
    if (v > bestN) { best = k; bestN = v; }
  }
  return best;
}

function timeClassFlavor(tc: string): string {
  switch (tc) {
    case 'bullet': return 'Sub-3 minutes. You play on instinct.';
    case 'blitz': return 'Three to ten minutes. The sweet spot.';
    case 'rapid': return 'You actually think before you move.';
    case 'daily':
    case 'classical': return 'You respect the game. The game respects you.';
    default: return 'You play the game your way.';
  }
}

function fmtSeconds(s: number): string {
  const total = Math.round(s);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const sec = total % 60;
  if (m < 60) return sec ? `${m}m ${sec}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const min = m % 60;
  return min ? `${h}h ${min}m` : `${h}h`;
}

const RESULT_LABELS: Record<string, string> = {
  win: 'Wins',
  checkmated: 'Lost to checkmate',
  resigned: 'You resigned',
  timeout: 'Lost on time',
  stalemate: 'Stalemate',
  agreed: 'Draw agreed',
  repetition: 'Draw by repetition',
  insufficient: 'Insufficient material',
  abandoned: 'Abandoned',
  timevsinsufficient: 'Timeout vs insufficient',
  '50move': '50-move rule',
  kingofthehill: 'King of the Hill',
  threecheck: 'Three-check',
  lose: 'Losses',
  draw: 'Draws',
};

function prettyResult(key: string): string {
  if (RESULT_LABELS[key]) return RESULT_LABELS[key];
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function topResults(results: Record<string, number>, n: number): { label: string; value: string }[] {
  return Object.entries(results)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([k, v]) => ({ label: prettyResult(k), value: `${v.toLocaleString()} games` }));
}

interface RatingJourney {
  timeClass: string;
  start: number;
  end: number;
  delta: number;
  startDate: string;
  endDate: string;
}

function ratingJourney(data: StatsResponse): RatingJourney | null {
  let best: { tc: string; points: { date: string; rating: number }[] } | null = null;
  for (const [tc, points] of Object.entries(data.last_game_ratings)) {
    const nums = points
      .map((p) => ({ date: p.date, rating: Number(p.my_rating) }))
      .filter((p) => !Number.isNaN(p.rating) && p.rating > 0);
    if (!best || nums.length > best.points.length) best = { tc, points: nums };
  }
  if (!best || best.points.length < 2) return null;
  const first = best.points[0];
  const lastPt = best.points[best.points.length - 1];
  return {
    timeClass: best.tc,
    start: first.rating,
    end: lastPt.rating,
    delta: lastPt.rating - first.rating,
    startDate: first.date,
    endDate: lastPt.date,
  };
}

function journeyHeadline(delta: number): string {
  if (delta >= 100) return 'You climbed. Big year for the rating graph.';
  if (delta >= 25) return 'Net positive. The work is paying off.';
  if (delta > -25) return 'Roughly where you started. Steady is a skill too.';
  if (delta > -100) return 'A dip this year. Every strong player has one.';
  return 'A rough patch. Ratings recover. So do you.';
}

// ─── Score computations ────────────────────────────────────────────────────

function computeDna(data: StatsResponse): { bars: ScoreBar[]; summary: string } {
  const total = data.total_games || 1;
  const white = data.my_openings.white_opening;
  const black = data.my_openings.black_opening;
  const tcc = data.timeclass_counts;
  const winRate = data.total_win_draw_loss.win / total;
  const leastTimeWonOk = !isBiggestGameError(data.biggest_games.least_time_won);

  let aggression = 50;
  if (includesAny(white, ['Sicilian', "King's Indian", 'Benoni', 'Dutch'])) aggression += 20;
  if (includesAny(black, ["King's Indian", 'Grunfeld', 'Nimzo'])) aggression += 20;
  if ((tcc.bullet ?? 0) > total * 0.3) aggression += 10;
  if (includesAny(white, ['London', 'Catalan', 'Petroff'])) aggression -= 15;
  if (includesAny(black, ['French', 'Caro', 'Slav', 'Petroff'])) aggression -= 15;

  let resilience = winRate * 60;
  if (data.longest_winning_streak > 5) resilience += 20;
  if (data.longest_losing_streak > 10) resilience -= 20;
  if (data.total_games > 300) resilience += 10;

  let clock = 50;
  if ((tcc.classical ?? 0) > 0 || (tcc.rapid ?? 0) > total * 0.3) clock += 30;
  if ((tcc.bullet ?? 0) > total * 0.5) clock -= 20;
  if (leastTimeWonOk) clock += 10;

  let theory = 40;
  if (!includesAny(white, ['Unknown', 'Irregular'])) theory += 30;
  if (!includesAny(black, ['Unknown', 'Irregular'])) theory += 20;
  if (white !== black) theory += 10;

  const volume = Math.min(100, Math.round((data.total_games / 1000) * 100));

  const bars: ScoreBar[] = [
    { name: 'Aggression', value: clamp(aggression) },
    { name: 'Resilience', value: clamp(resilience) },
    { name: 'Clock discipline', value: clamp(clock) },
    { name: 'Opening theory', value: clamp(theory) },
    { name: 'Volume', value: clamp(volume) },
  ];

  const agg = clamp(aggression);
  const res = clamp(resilience);
  const th = clamp(theory);
  const cl = clamp(clock);
  let summary = 'Balanced player with room to specialize.';
  if (agg > 60 && res > 60) summary = 'High-octane competitor.';
  else if (th > 60 && cl > 60) summary = 'Technically disciplined.';
  else if (clamp(volume) > 80) summary = 'Pure volume player.';

  return { bars, summary };
}

function computePsychology(data: StatsResponse): { overall: number; verdict: string; bars: ScoreBar[] } {
  const total = data.total_games || 1;
  const winRate = data.total_win_draw_loss.win / total;
  const tcc = data.timeclass_counts;
  const leastTimeWonOk = !isBiggestGameError(data.biggest_games.least_time_won);

  let clutch = 0;
  if (leastTimeWonOk) clutch += 50;
  if (winRate > 0.55) clutch += 30;
  if (data.longest_winning_streak > 7) clutch += 20;
  clutch = clamp(clutch);

  let composure = 80 - data.longest_losing_streak * 4;
  if (winRate > 0.5) composure += 20;
  composure = clamp(composure);

  let adaptability = 50;
  for (const tc of ['bullet', 'blitz', 'rapid', 'classical'] as const) {
    if ((tcc[tc] ?? 0) > 10) adaptability += 15;
  }
  adaptability = clamp(adaptability);

  const streakRatio = data.longest_winning_streak / Math.max(data.longest_losing_streak, 1);
  const consistency = clamp(streakRatio * 20 + winRate * 60);

  const overall = Math.round((clutch + composure + adaptability + consistency) / 4);

  let verdict = 'The mental side is the next frontier.';
  if (overall >= 80) verdict = "Elite mental game. Pressure doesn't rattle you.";
  else if (overall >= 65) verdict = 'Mentally solid. You hold up under strain.';
  else if (overall >= 50) verdict = 'Some tilt risk. Streaks affect your headspace.';

  const bars: ScoreBar[] = [
    { name: 'Clutch factor', value: clutch },
    { name: 'Composure', value: composure },
    { name: 'Adaptability', value: adaptability },
    { name: 'Consistency', value: consistency },
  ];

  return { overall, verdict, bars };
}

function computePersonality(data: StatsResponse): { archetype: string; description: string; traits: string[] } {
  const total = data.total_games || 1;
  const winRate = data.total_win_draw_loss.win / total;
  const tcc = data.timeclass_counts;
  const blitzFrac = (tcc.blitz ?? 0) / total;
  const bulletFrac = (tcc.bullet ?? 0) / total;
  const rapidFrac = (tcc.rapid ?? 0) / total;
  const whiteOp = data.my_openings.white_opening.toLowerCase();
  const blackOp = data.my_openings.black_opening.toLowerCase();

  if (bulletFrac > 0.5) {
    return {
      archetype: 'The Bullet Assassin',
      description: "You think in instants. Most players cannot keep up with that pace.",
      traits: ['Speed', 'Intuition', 'Reflex'],
    };
  }
  if (blitzFrac > 0.6) {
    return {
      archetype: 'The Blitz Specialist',
      description: "Blitz is your arena. Three to ten minutes, and you're dangerous.",
      traits: ['Rhythm', 'Pattern recognition', 'Clock awareness'],
    };
  }
  if (rapidFrac > 0.5 || (tcc.classical ?? 0) > 20) {
    return {
      archetype: 'The Classical Scholar',
      description: 'You take your time. Every move has a reason.',
      traits: ['Depth', 'Calculation', 'Patience'],
    };
  }
  if (includesAny(whiteOp, ['sicilian', "king's indian", 'benoni', 'dutch', 'grand prix'])) {
    return {
      archetype: 'The Tactician',
      description: "You play for complications. Give you a chaotic position and you'll find a way.",
      traits: ['Sharp lines', 'Initiative', 'Complications'],
    };
  }
  if (includesAny(whiteOp, ['london', 'catalan', 'reti', 'english']) ||
      includesAny(blackOp, ['caro', 'slav', 'petroff', 'berlin'])) {
    return {
      archetype: 'The Positional Grinder',
      description: 'You squeeze. No flashy sacrifices, just relentless pressure until they crack.',
      traits: ['Structure', 'Endgames', 'Long-game thinking'],
    };
  }
  if (blackOp.includes('french') || blackOp.includes('caro') || (winRate < 0.45 && total > 150)) {
    return {
      archetype: 'The Fortress',
      description: 'You build walls. Breaking you down is a project most players give up on.',
      traits: ['Defense', 'Resilience', 'Counterplay'],
    };
  }
  if (winRate >= 0.54 && data.longest_losing_streak >= 6) {
    return {
      archetype: 'The Comeback Kid',
      description: "You've been down bad. You came back anyway. That's a skill.",
      traits: ['Recovery', 'Mental strength', 'Never tilts'],
    };
  }
  if (data.longest_winning_streak >= 10) {
    return {
      archetype: 'The Streaky Genius',
      description: "When you're hot, you're untouchable. The question is how often you get there.",
      traits: ['Peak performance', 'Momentum', 'Hot and cold'],
    };
  }
  if (data.total_games > 500) {
    return {
      archetype: 'The Volume Player',
      description: 'You put in the reps. At this volume, patterns become instinct.',
      traits: ['Dedication', 'Experience', 'High output'],
    };
  }
  return {
    archetype: 'The Chess Enthusiast',
    description: 'You play for the love of the game. That is the best reason.',
    traits: ['Curiosity', 'Improvement', 'Passion'],
  };
}

// ─── Card builder ──────────────────────────────────────────────────────────

function buildCards(data: StatsResponse, year: string): Card[] {
  const cards: Card[] = [];
  const total = data.total_games || 1;
  const { win, draw, lose } = data.total_win_draw_loss;
  const winPct = Math.round((win / total) * 100);

  cards.push({ slug: 'intro', bg: '#0d1117', kind: 'intro', username: data.my_username, year });

  cards.push({
    slug: 'games', bg: '#1a1a2e', kind: 'hero',
    eyebrow: 'Total games played',
    hero: data.total_games.toLocaleString(),
    headline: gamesFlavor(data.total_games),
    flavor: `That's ${data.total_moves.toLocaleString()} total moves across all those games.`,
  });

  cards.push({
    slug: 'winrate', bg: '#0f3460', kind: 'hero',
    eyebrow: 'Win rate',
    hero: `${winPct}%`,
    headline: winRateFlavor(winPct),
    flavor: `${win} wins, ${draw} draws, ${lose} losses`,
  });

  cards.push({
    slug: 'time-spent', bg: '#533483', kind: 'split',
    left: { label: 'Time at the board', value: data.total_time_spent.total_time },
    right: { label: '% of the year', value: `${data.total_time_spent.percentage_of_year.toFixed(2)}%` },
    headline: 'Time is the real currency of chess.',
    flavor: timeFlavor(data.total_time_spent.total_time_in_hours),
  });

  cards.push({
    slug: 'peak-rating', bg: '#1b4332', kind: 'hero',
    eyebrow: 'Your peak rating',
    hero: data.highest_rating.rating.toString(),
    headline: `Peak ${data.highest_rating.time_control} rating`,
    flavor: `Achieved on ${data.highest_rating.date}.`,
    secondary: ratingFlavor(data.highest_rating.rating),
  });

  const journey = ratingJourney(data);
  if (journey) {
    const sign = journey.delta >= 0 ? '+' : '';
    cards.push({
      slug: 'rating-journey', bg: '#014f86', kind: 'split',
      left: { label: `${journey.timeClass} start`, value: journey.start.toString() },
      right: { label: `${journey.timeClass} finish`, value: journey.end.toString() },
      headline: journeyHeadline(journey.delta),
      flavor: `${sign}${journey.delta} rating over the year, from ${journey.startDate} to ${journey.endDate}.`,
    });
  }

  cards.push({
    slug: 'openings', bg: '#2d6a4f', kind: 'list',
    headline: 'Your opening identity',
    items: [
      { label: 'As White', value: data.my_openings.white_opening },
      { label: 'As Black', value: data.my_openings.black_opening },
    ],
    styleTag: openingStyleTag(data.my_openings.white_opening, data.my_openings.black_opening),
  });

  cards.push({
    slug: 'streaks', bg: '#b5451b', kind: 'split',
    left: { label: 'Longest win streak', value: `${data.longest_winning_streak} in a row` },
    right: { label: 'Longest loss streak', value: `${data.longest_losing_streak} in a row` },
    headline: streakHeadline(data.longest_winning_streak, data.longest_losing_streak),
    flavor: 'Every streak ends. Every streak starts again.',
  });

  const upset = data.biggest_games.upset;
  if (!isBiggestGameError(upset)) {
    cards.push({
      slug: 'upset', bg: '#c9184a', kind: 'hero',
      eyebrow: 'Biggest upset',
      hero: `${upset.opp_rating} rated`,
      headline: `You beat @${upset.opp_username}`,
      flavor: `In ${upset.time_class} chess. On ${upset.date}.`,
      link: upset.link,
    });
  }

  const checkmate = data.biggest_games.checkmate;
  if (!isBiggestGameError(checkmate)) {
    cards.push({
      slug: 'checkmate', bg: '#370617', kind: 'hero',
      eyebrow: 'Most dramatic win',
      hero: 'Checkmate',
      headline: `Delivered in ${checkmate.my_num_moves} moves against @${checkmate.opp_username}`,
      flavor: `${checkmate.opp_rating} rated. You found it.`,
      link: checkmate.link,
    });
  }

  const longest = data.biggest_games.longest;
  if (!isBiggestGameError(longest)) {
    cards.push({
      slug: 'longest-game', bg: '#3a0ca3', kind: 'hero',
      eyebrow: 'Your longest battle',
      hero: fmtSeconds(longest.time_spent),
      headline: `A marathon against @${longest.opp_username}`,
      flavor: `${longest.time_class} chess, ${longest.opp_rating} rated. On ${longest.date}.`,
      link: longest.link,
    });
  }

  const moves = data.biggest_games.moves;
  if (!isBiggestGameError(moves)) {
    cards.push({
      slug: 'most-moves', bg: '#4361ee', kind: 'hero',
      eyebrow: 'Most moves in one game',
      hero: `${moves.my_num_moves} moves`,
      headline: `You went the distance against @${moves.opp_username}`,
      flavor: `Neither side blinked early. ${moves.time_class} chess on ${moves.date}.`,
      link: moves.link,
    });
  }

  const clutch = data.biggest_games.least_time_won;
  if (!isBiggestGameError(clutch)) {
    cards.push({
      slug: 'clock-clutch', bg: '#5a189a', kind: 'hero',
      eyebrow: 'Closest clock call',
      hero: `${fmtSeconds(clutch.my_time_left)} left`,
      headline: `You won with the flag about to fall against @${clutch.opp_username}`,
      flavor: `${clutch.time_class} chess, ${clutch.opp_rating} rated. Nerves of steel.`,
      link: clutch.link,
    });
  }

  cards.push({
    slug: 'busiest-day', bg: '#1d3557', kind: 'hero',
    eyebrow: 'Busiest single day',
    hero: data.most_time_spent_day_dict.date,
    headline: `You played ${data.most_time_spent_day_dict.time_spent} on this one day.`,
    flavor: busiestFlavor(data.most_time_spent_day_dict.time_spent),
  });

  const opp0 = data.most_played_opponent.Opponent['0'];
  if (opp0) {
    const games0 = data.most_played_opponent.Games_Played['0'];
    const opp1 = data.most_played_opponent.Opponent['1'];
    const games1 = data.most_played_opponent.Games_Played['1'];
    cards.push({
      slug: 'nemesis', bg: '#212529', kind: 'hero',
      eyebrow: 'Your most frequent opponent',
      hero: `@${opp0}`,
      headline: `${games0} games against them.`,
      flavor: 'Whether you loved playing them or hated it, you kept coming back.',
      secondary: opp1 ? `Also vs @${opp1} (${games1} games)` : undefined,
    });
  }

  const resultItems = topResults(data.total_results, 4);
  if (resultItems.length > 0) {
    cards.push({
      slug: 'result-breakdown', bg: '#780000', kind: 'list',
      headline: 'How your games ended',
      items: resultItems,
      styleTag: 'Every result tells a story',
    });
  }

  // Time control: top 3 by games played
  const tcTable = data.timecontrol_counts;
  const tcEntries = Object.keys(tcTable.Time_Control)
    .map((k) => ({
      label: tcTable.Time_Control[k],
      games: tcTable.Games_Played[k] ?? 0,
    }))
    .sort((a, b) => b.games - a.games)
    .slice(0, 3);
  if (tcEntries.length > 0) {
    cards.push({
      slug: 'time-control', bg: '#023e8a', kind: 'list',
      headline: `You're a ${tcEntries[0].label} player.`,
      items: tcEntries.map((e) => ({ label: e.label, value: `${e.games} games` })),
      styleTag: timeClassFlavor(topTimeClass(data.timeclass_counts)),
    });
  }

  if (data.total_promotions > 0 || data.total_en_passant > 0) {
    cards.push({
      slug: 'quirks', bg: '#4d194d', kind: 'split',
      left: { label: 'Pawns promoted', value: data.total_promotions.toLocaleString() },
      right: { label: 'En passants', value: data.total_en_passant.toLocaleString() },
      headline: 'The little moments that made you smile.',
      flavor: data.total_en_passant > 0
        ? 'En passant: the rarest bragging rights in chess.'
        : 'Every promotion is a pawn that made it all the way.',
    });
  }

  const dna = computeDna(data);
  cards.push({ slug: 'chess-dna', bg: '#1c0a4a', kind: 'dna', summary: dna.summary, bars: dna.bars });

  const psych = computePsychology(data);
  cards.push({
    slug: 'psychology-score', bg: '#2b0a3d', kind: 'psychology',
    overall: psych.overall, verdict: psych.verdict, bars: psych.bars,
  });

  const personality = computePersonality(data);
  cards.push({
    slug: 'personality', bg: '#240046', kind: 'personality',
    archetype: personality.archetype, description: personality.description, traits: personality.traits,
  });

  cards.push({
    slug: 'share', bg: '#10131a', kind: 'share',
    username: data.my_username,
    avatar: data.my_avatar || DEFAULT_AVATAR,
    year,
    archetype: personality.archetype,
    stats: [
      { label: 'Games', value: data.total_games.toLocaleString() },
      { label: 'Win rate', value: `${winPct}%` },
      { label: 'Peak rating', value: data.highest_rating.rating.toString() },
      { label: 'Favorite format', value: tcEntries[0]?.label ?? '—' },
    ],
  });

  cards.push({
    slug: 'outro', bg: '#2D6056', kind: 'outro',
    username: data.my_username, avatar: data.my_avatar || DEFAULT_AVATAR, year,
  });

  return cards;
}

// ─── Score bars (animated on activation) ───────────────────────────────────

function barColor(v: number): string {
  if (v < 40) return '#c9184a';
  if (v < 70) return '#f6c90e';
  return '#43e97b';
}

function ScoreBars({ bars, active }: { bars: ScoreBar[]; active: boolean }) {
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    if (!active) { setGrown(false); return; }
    const t = window.setTimeout(() => setGrown(true), 50);
    return () => window.clearTimeout(t);
  }, [active]);

  return (
    <div className="wm-score-grid">
      {bars.map((b) => (
        <div key={b.name} className="wm-score-row">
          <span className="wm-score-label">{b.name}</span>
          <span className="wm-score-bar-track">
            <span
              className="wm-score-bar-fill"
              style={{ width: grown ? `${b.value}%` : '0%', background: barColor(b.value) }}
            />
          </span>
          <span className="wm-score-value">{b.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Shareable, themeable card ─────────────────────────────────────────────

interface ShareTheme {
  id: string;
  name: string;
  bg: string;
  swatch: string;
  fg: string;
  sub: string;
  accent: string;
  statBg: string;
  ring: string;
}

const SHARE_THEMES: ShareTheme[] = [
  {
    id: 'midnight', name: 'Midnight',
    bg: 'linear-gradient(160deg, #0d1117 0%, #1b2340 100%)', swatch: '#141a2e',
    fg: '#ffffff', sub: 'rgba(255,255,255,0.55)', accent: '#43e97b',
    statBg: 'rgba(255,255,255,0.06)', ring: 'rgba(67,233,123,0.55)',
  },
  {
    id: 'emerald', name: 'Emerald',
    bg: 'linear-gradient(160deg, #14532d 0%, #2d6056 100%)', swatch: '#2d6056',
    fg: '#ffffff', sub: 'rgba(255,255,255,0.6)', accent: '#a7f3d0',
    statBg: 'rgba(255,255,255,0.09)', ring: 'rgba(167,243,208,0.6)',
  },
  {
    id: 'sunset', name: 'Sunset',
    bg: 'linear-gradient(160deg, #7c2d12 0%, #b5451b 100%)', swatch: '#b5451b',
    fg: '#fff7ef', sub: 'rgba(255,255,255,0.62)', accent: '#ffd166',
    statBg: 'rgba(255,255,255,0.10)', ring: 'rgba(255,209,102,0.65)',
  },
  {
    id: 'grape', name: 'Grape',
    bg: 'linear-gradient(160deg, #240046 0%, #5a189a 100%)', swatch: '#5a189a',
    fg: '#ffffff', sub: 'rgba(255,255,255,0.6)', accent: '#c77dff',
    statBg: 'rgba(255,255,255,0.09)', ring: 'rgba(199,125,255,0.6)',
  },
  {
    id: 'paper', name: 'Paper',
    bg: 'linear-gradient(160deg, #f5f3e6 0%, #e5e0cd 100%)', swatch: '#e5e0cd',
    fg: '#1b2b27', sub: 'rgba(27,43,39,0.55)', accent: '#2d6056',
    statBg: 'rgba(0,0,0,0.05)', ring: 'rgba(45,96,86,0.6)',
  },
];

async function waitForImages(el: HTMLElement) {
  const imgs = Array.from(el.getElementsByTagName('img'));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) return resolve();
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }),
    ),
  );
}

function ShareCard({
  username, avatar, year, archetype, stats,
}: {
  username: string; avatar: string; year: string;
  archetype: string; stats: { label: string; value: string }[];
}) {
  const [themeId, setThemeId] = useState(SHARE_THEMES[0].id);
  const [working, setWorking] = useState<'download' | 'share' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const posterRef = useRef<HTMLDivElement>(null);
  const theme = SHARE_THEMES.find((t) => t.id === themeId) ?? SHARE_THEMES[0];

  async function capture(): Promise<HTMLCanvasElement | null> {
    const el = posterRef.current;
    if (!el) return null;
    await waitForImages(el);
    const scale = typeof window !== 'undefined' && window.innerWidth < 768 ? 1.5 : 2;
    return html2canvas(el, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: theme.swatch,
      logging: false,
    });
  }

  const filename = `${username}_ChessWrapped_${year}_${theme.id}.png`;

  async function handleDownload() {
    setNotice(null);
    setWorking('download');
    try {
      const canvas = await capture();
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      setNotice('Saved to your downloads.');
      window.setTimeout(() => setNotice(null), 3500);
    } catch (err) {
      console.error('Wrapped card download failed:', err);
      setNotice('Could not save. Try another theme or screenshot instead.');
    } finally {
      setWorking(null);
    }
  }

  async function handleShare() {
    setNotice(null);
    setWorking('share');
    try {
      const canvas = await capture();
      if (!canvas) return;
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png', 1.0),
      );
      if (!blob) {
        setNotice('Could not prepare an image.');
        return;
      }
      const file = new File([blob], filename, { type: 'image/png' });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: `${username}'s Chess Wrapped ${year}`,
          text: 'My chess year in review.',
          files: [file],
        });
        setNotice('Shared.');
      } else {
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          setNotice('Copied to clipboard.');
        } catch {
          setNotice('Sharing not supported here. Use Download.');
        }
      }
      window.setTimeout(() => setNotice(null), 3500);
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      console.error('Wrapped card share failed:', err);
      setNotice('Could not share. Try Download.');
    } finally {
      setWorking(null);
    }
  }

  return (
    <>
      <div className="wm-eyebrow">Your shareable card</div>

      <div
        className="wm-share-poster"
        ref={posterRef}
        style={{ background: theme.bg, color: theme.fg }}
      >
        <span className="wm-poster-eyebrow" style={{ color: theme.sub }}>
          Chess Wrapped {year}
        </span>
        <img
          src={avatar}
          alt={username}
          className="wm-poster-avatar"
          style={{ borderColor: theme.ring }}
          onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_AVATAR; }}
        />
        <span className="wm-poster-name">{username}</span>
        <span className="wm-poster-archetype" style={{ color: theme.accent }}>{archetype}</span>
        <div className="wm-poster-stats">
          {stats.map((s) => (
            <div key={s.label} className="wm-poster-stat" style={{ background: theme.statBg }}>
              <span className="wm-poster-stat-value" style={{ color: theme.accent }}>{s.value}</span>
              <span className="wm-poster-stat-label" style={{ color: theme.sub }}>{s.label}</span>
            </div>
          ))}
        </div>
        <span className="wm-poster-foot" style={{ color: theme.sub }}>chesslytics</span>
      </div>

      <div className="wm-theme-row" role="group" aria-label="Card theme">
        {SHARE_THEMES.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`wm-theme-chip${t.id === themeId ? ' wm-theme-chip--active' : ''}`}
            style={{ background: t.swatch }}
            aria-label={`${t.name} theme`}
            aria-pressed={t.id === themeId}
            onClick={() => setThemeId(t.id)}
          />
        ))}
      </div>

      <div className="wm-share-actions">
        <button type="button" className="wm-share-btn" onClick={handleDownload} disabled={working !== null}>
          {working === 'download' ? 'Saving…' : 'Download'}
        </button>
        <button type="button" className="wm-share-btn wm-share-btn--ghost" onClick={handleShare} disabled={working !== null}>
          {working === 'share' ? 'Preparing…' : 'Share'}
        </button>
      </div>

      {notice && <p className="wm-share-hint" role="status">{notice}</p>}
    </>
  );
}

// ─── Card renderer ─────────────────────────────────────────────────────────

function CardBody({ card, active, onClose }: { card: Card; active: boolean; onClose: () => void }) {
  switch (card.kind) {
    case 'intro':
      return (
        <>
          <div className="wm-intro-pawn" aria-hidden>♟</div>
          <div className="wm-intro-username">{card.username}</div>
          <div className="wm-intro-year">Chess Wrapped {card.year}</div>
          <div className="wm-intro-hint">Swipe or use arrow keys</div>
        </>
      );

    case 'hero':
      return (
        <>
          <div className="wm-eyebrow">{card.eyebrow}</div>
          <div className="wm-hero">{card.hero}</div>
          <h2 className="wm-headline">{card.headline}</h2>
          <p className="wm-flavor">{card.flavor}</p>
          {card.secondary && <p className="wm-flavor">{card.secondary}</p>}
          {card.link && (
            <a className="wm-game-link" href={card.link} target="_blank" rel="noopener noreferrer">
              View game →
            </a>
          )}
        </>
      );

    case 'split':
      return (
        <>
          <div className="wm-split">
            <div className="wm-split-side">
              <span className="wm-split-value">{card.left.value}</span>
              <span className="wm-split-label">{card.left.label}</span>
            </div>
            <div className="wm-split-side">
              <span className="wm-split-value">{card.right.value}</span>
              <span className="wm-split-label">{card.right.label}</span>
            </div>
          </div>
          <h2 className="wm-headline">{card.headline}</h2>
          <p className="wm-flavor">{card.flavor}</p>
        </>
      );

    case 'list':
      return (
        <>
          <h2 className="wm-headline">{card.headline}</h2>
          <ul className="wm-list">
            {card.items.map((it) => (
              <li key={it.label} className="wm-list-item">
                <span className="wm-list-label">{it.label}</span>
                <span className="wm-list-value">{it.value}</span>
              </li>
            ))}
          </ul>
          {card.styleTag && <span className="wm-opening-style">{card.styleTag}</span>}
        </>
      );

    case 'dna':
      return (
        <>
          <div className="wm-eyebrow">Chess DNA</div>
          <ScoreBars bars={card.bars} active={active} />
          <p className="wm-flavor">{card.summary}</p>
        </>
      );

    case 'psychology':
      return (
        <>
          <div className="wm-eyebrow">Chess psychology score</div>
          <div className="wm-overall-score">{card.overall}</div>
          <p className="wm-score-verdict">{card.verdict}</p>
          <ScoreBars bars={card.bars} active={active} />
        </>
      );

    case 'personality':
      return (
        <>
          <div className="wm-eyebrow">Your chess personality</div>
          <h2 className="wm-archetype">{card.archetype}</h2>
          <p className="wm-archetype-desc">{card.description}</p>
          <div className="wm-trait-tags">
            {card.traits.map((t) => (
              <span key={t} className="wm-trait-tag">{t}</span>
            ))}
          </div>
        </>
      );

    case 'share':
      return (
        <ShareCard
          username={card.username}
          avatar={card.avatar}
          year={card.year}
          archetype={card.archetype}
          stats={card.stats}
        />
      );

    case 'outro':
      return (
        <>
          <img
            src={card.avatar}
            alt={card.username}
            className="wm-outro-avatar"
            onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_AVATAR; }}
          />
          <div className="wm-outro-username">{card.username}</div>
          <div className="wm-outro-year">{card.year}</div>
          <button type="button" className="wm-done-btn" onClick={onClose}>Done</button>
          <p className="wm-share-hint">Screenshot and share your chess year.</p>
        </>
      );
  }
}

// ─── Modal ─────────────────────────────────────────────────────────────────

export default function WrappedModal({ data, year, onClose }: WrappedModalProps) {
  const cards = useMemo(() => buildCards(data, year), [data, year]);
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const last = cards.length - 1;
  const goNext = () => setIndex((i) => Math.min(i + 1, last));
  const goPrev = () => setIndex((i) => Math.max(i - 1, 0));

  // Keyboard nav + focus restore + scroll lock
  useEffect(() => {
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    overlayRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowRight') { setIndex((i) => Math.min(i + 1, cards.length - 1)); }
      if (e.key === 'ArrowLeft') { setIndex((i) => Math.max(i - 1, 0)); }
    };
    window.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      restoreFocusRef.current?.focus?.();
    };
  }, [cards.length, onClose]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    if (delta < -50) goNext();
    else if (delta > 50) goPrev();
    touchStartX.current = null;
  };

  const modal = (
    <div
      ref={overlayRef}
      className="wm-overlay"
      style={{ backgroundColor: cards[index].bg }}
      role="dialog"
      aria-modal="true"
      aria-label={`Chess Wrapped ${year}`}
      tabIndex={-1}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Progress bar */}
      <div className="wm-progress" aria-hidden>
        {cards.map((c, i) => (
          <span
            key={c.slug}
            className={`wm-progress-seg${i === index ? ' wm-progress-seg--active' : ''}`}
          />
        ))}
      </div>

      {/* Close */}
      <button type="button" className="wm-close" onClick={onClose} aria-label="Close">
        ✕
      </button>

      {/* Cards */}
      <div className="wm-cards-viewport">
        {cards.map((card, i) => {
          const pos = i === index ? 'active' : i < index ? 'prev' : 'next';
          return (
            <div key={card.slug} className={`wm-card wm-card--${pos}`} aria-hidden={i !== index}>
              <CardBody card={card} active={i === index} onClose={onClose} />
            </div>
          );
        })}
      </div>

      {/* Prev nav */}
      {index > 0 && (
        <button type="button" className="wm-nav wm-nav--prev" onClick={goPrev} aria-label="Previous card">
          ‹
        </button>
      )}

      {/* Next nav or Done on last card */}
      {index < last ? (
        <button type="button" className="wm-nav wm-nav--next" onClick={goNext} aria-label="Next card">
          ›
        </button>
      ) : null}
    </div>
  );

  return createPortal(modal, document.body);
}
